// Public links temporarily disabled — `useState` was only used by the
// public link state below; restore it when re-enabling
import { useEffect, useRef } from 'react'

import { ErrorBoundary } from '@/components/ErrorBoundary'
import { GoogleLogin } from '@/components/GoogleLogin'
import { MultiSelect } from '@/components/MultiSelect'
import { CreateLinkInput } from '@/components/CreateLinkInput'
import { LinkTypeSelector } from '@/components/LinkTypeSelector'
import { ShareLinkPopover } from '@/components/ShareLinkPopover'
// Public links temporarily disabled
// import { PublicLink } from '@/components/PublicLink'
import { Button } from '@/components/Button'
import { Label } from '@/components/Label'
import { useForm, ValidationRules } from '@/hooks/useForm'
import { useCreateLink } from '@/hooks/useCreateLink'
import { useOrgTeams } from '@/hooks/useOrgTeams'
import { useUser } from '@/hooks/useUser'
import { useToast } from '@/hooks/Toast'
import { slugValidation, destinationValidation } from '@/utils/validations'
import { UserIcon, CompanyIcon, EnterIcon } from '@/icons'
import { Link } from '@/types'

import s from './CreateLinkForm.module.css'

export type FormValues = {
  slug: string
  destinations: string[]
  type: 'organization' | 'team' | 'personal'
  teamIds?: { label: string; value: string }[]
  shareWith?: string
}

const validationRules: ValidationRules = {
  slug: slugValidation,
  destinations: {
    dynamicValidation: destinationValidation,
  },
}

type Props = {
  defaultValues: {
    slug: string
    destinations: string[]
  }
  actionLabel: string
  onSuccess: (link: Link) => void
  onCancel?: () => void
}

export const CreateLinkForm = ({
  defaultValues,
  actionLabel,
  onSuccess,
  onCancel,
}: Props) => {
  const { user } = useUser()
  const hasOrgAccess = user?.hasOrgAccess
  const hasPersonalLinksAccess = user?.hasPersonalLinksAccess
  const orgId = user?.organizationId

  const { data: teams } = useOrgTeams(orgId)
  const formRef = useRef<HTMLFormElement>(null)
  const createLinkMutation = useCreateLink()
  const toast = useToast()

  const {
    values,
    errors,
    setFieldValue,
    isSubmitting,
    handleChange,
    handleDynamicChange,
    addDynamicField,
    removeDynamicField,
    handleSubmit,
  } = useForm({
    initialValues: {
      slug: defaultValues.slug,
      destinations: defaultValues.destinations,
      type: 'personal',
    } as FormValues,
    validationRules: validationRules,
    formRef: formRef,
  })

  // Public links temporarily disabled
  // const [publicLink, setPublicLink] = useState('')

  const onSubmit = async (formValues: FormValues) => {
    const urls = formValues.destinations

    const duplicates = urls.filter((url, i) => urls.indexOf(url) !== i)
    if (duplicates.length > 0) {
      toast.error('Please remove duplicate destination URLs.')
      return
    }

    const cleanedUrls = urls.map((url) => {
      const hasProtocol =
        url.startsWith('https://') || url.startsWith('http://')
      return hasProtocol ? url : `https://${url}`
    })

    let _data: any = {
      slug: formValues.slug,
      destinations: cleanedUrls,
      type: formValues.type,
    }

    // TODO: Implement org selection
    if (formValues.type === 'organization') {
      _data = {
        ..._data,
        organizationId: orgId,
      }

      if (
        formValues.type === 'organization' &&
        formValues.teamIds?.length &&
        (formValues.teamIds.length === 1 ||
          formValues.teamIds.length !== teams?.length)
      ) {
        _data = {
          ..._data,
          type: 'team',
          teamIds: formValues.teamIds.map((team) => team.value),
        }
      }
    }

    // Public links temporarily disabled
    // if (publicLink) {
    //   const match = publicLink.match(/\?(.*)$/)
    //   const code = match ? match[1] : undefined

    //   _data = {
    //     ..._data,
    //     publicLinkPasscode: code,
    //   }
    // }

    if (formValues.shareWith) {
      _data = {
        ..._data,
        shareWith: formValues.shareWith,
      }
    }

    try {
      const linkResponse = await createLinkMutation.mutateAsync(_data)
      onSuccess({
        ...linkResponse,
        destinations: cleanedUrls,
      })
    } catch {
      toast.error('Something went wrong. Try again?')
    }
  }

  useEffect(() => {
    const linkTypePref = localStorage.getItem('type-pref')
    if (linkTypePref) {
      setFieldValue('type', linkTypePref as 'organization' | 'personal')
    } else {
      if (hasPersonalLinksAccess) {
        setFieldValue('type', 'personal')
      } else if (hasOrgAccess) {
        setFieldValue('type', 'organization')
      }
    }
  }, [hasPersonalLinksAccess, hasOrgAccess])

  return (
    <ErrorBoundary>
      <form
        ref={formRef}
        className={s.form}
        noValidate
        onSubmit={handleSubmit((values) => onSubmit(values))}
      >
        <CreateLinkInput
          onChange={handleChange}
          onUrlChange={handleDynamicChange}
          errors={errors}
          values={values}
          addDynamicField={addDynamicField}
          removeDynamicField={removeDynamicField}
          defaultValues={defaultValues}
        />

        {/* Public links temporarily disabled
        <PublicLink slug={values.slug} onUpdatePublicLink={setPublicLink} /> */}

        <div className={s.radioGroup}>
          <Label>Link Type</Label>
          <div>
            <LinkTypeSelector
              title="Personal"
              description="Your private collection of links and favorites."
              icon={UserIcon}
              value="personal"
              checked={values.type === 'personal'}
              disabled={!hasPersonalLinksAccess}
              onChange={(e) => {
                localStorage.setItem('type-pref', 'personal')
                handleChange(e)
              }}
            >
              {user && !hasPersonalLinksAccess && (
                <GoogleLogin ref="link_personal" isLinkingAccount>
                  Connect Personal Email
                </GoogleLogin>
              )}
            </LinkTypeSelector>

            <LinkTypeSelector
              title="Work"
              description="Shared links with teammates — projects, docs, dashboards."
              icon={CompanyIcon}
              value="organization"
              checked={values.type === 'organization'}
              disabled={!hasOrgAccess}
              onChange={(e) => {
                localStorage.setItem('type-pref', 'organization')
                handleChange(e)
              }}
            >
              {hasOrgAccess ? (
                <MultiSelect
                  items={
                    teams?.map((team) => ({
                      label: team.name,
                      value: team.id,
                    })) ?? []
                  }
                  placeholder="Select Teams"
                  selectedTextFn={(selectedItems) =>
                    selectedItems.length === 0
                      ? 'Teams: All'
                      : selectedItems.length === teams?.length
                      ? 'Teams: All'
                      : selectedItems.length === 1
                      ? `Team: ${selectedItems[0].label}`
                      : `Teams: ${selectedItems.length} selected`
                  }
                  onChange={(value) => setFieldValue('teamIds', value)}
                />
              ) : user ? (
                <GoogleLogin ref="link_work" isLinkingAccount>
                  Connect Work Email
                </GoogleLogin>
              ) : null}
            </LinkTypeSelector>
          </div>
        </div>

        <footer className={s.footer}>
          <ShareLinkPopover
            onChange={handleChange}
            onReset={() => setFieldValue('shareWith', '')}
          />
          <div>
            {!!onCancel && (
              <Button variant="ghost" onClick={onCancel}>
                Cancel
              </Button>
            )}
            &nbsp;&nbsp;
            <Button
              variant="primary"
              type="submit"
              iconRight={EnterIcon}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : actionLabel}
            </Button>
          </div>
        </footer>
      </form>
    </ErrorBoundary>
  )
}
